import { useTheme } from '@mui/material';
import DSBox from '../../../design-system/DSBox';
import { DSTypography } from '../../../design-system/Typography';
import CountryPerformanceItem from './CountryPerformanceItem';

export type CountryData = {
  countryCode: string;
  percentage: string;
  total: number;
};

export type TopPerformingCountriesProps = {
  data: CountryData[];
  isEmpty?: boolean;
  hasError?: boolean;
};

const TopPerformingCountries = ({
  data,
  isEmpty,
  hasError,
}: TopPerformingCountriesProps) => {
  const { spacing, palette } = useTheme();

  const renderContent = () => {
    if (hasError) {
      return (
        <DSBox
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: spacing(40),
          }}
        >
          <DSTypography
            variant="body1"
            sx={{ color: palette.error.main, textAlign: 'center' }}
          >
            Unable to load country data
          </DSTypography>
        </DSBox>
      );
    }

    if (isEmpty || data.length === 0) {
      return (
        <DSBox
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: spacing(40),
          }}
        >
          <DSTypography
            variant="body1"
            sx={{ color: palette.text.secondary, textAlign: 'center' }}
          >
            No country data available yet
          </DSTypography>
        </DSBox>
      );
    }

    return (
      <>
        <DSTypography variant="h6" sx={{ marginBottom: spacing(3) }}>
          Top Performing Countries
        </DSTypography>

        <DSBox
          sx={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr',
            gap: spacing(2),
            marginBottom: spacing(2),
          }}
        >
          <DSTypography variant="body2">Countries</DSTypography>
          <DSTypography variant="body2">Percentage</DSTypography>
          <DSTypography variant="body2">Total</DSTypography>
        </DSBox>

        <DSBox
          sx={{
            overflowY: 'auto',
            flex: 1,
            paddingRight: spacing(1),
          }}
        >
          {data.map((country, index) => (
            <CountryPerformanceItem
              key={index}
              countryCode={country.countryCode}
              percentage={country.percentage}
              total={country.total}
            />
          ))}
        </DSBox>
      </>
    );
  };

  return (
    <DSBox
      sx={{
        backgroundColor: palette.paper.elevation5,
        borderRadius: spacing(2),
        padding: spacing(4),
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: spacing(50),
      }}
    >
      {renderContent()}
    </DSBox>
  );
};

export default TopPerformingCountries;
